import React, { useEffect } from 'react';
import {
  Footer,
  UploadedDocument,
  PDFPreview,
  Summary as SummarySection,
  LoadingSpinner,
  TextInput,
  UploadedText,
  InvoiceExtractorSummary,
  CatastropheDetection,
  NDAGenerator,
  DocumentCompareV2
} from 'app/components';
import { TranslatorPage } from 'pages/Translator';
import { useAppSelector } from 'app';
import { getIsLoading, getService } from 'app/redux';
import styles from './Summary.module.scss';
import { scrollToTop } from 'app/utils';
import { PDFHighlightPreview } from 'app/components/Sections/PDFHighlightPreview';
import {
  getCurrentFile,
  getHighlightCoords,
  getSelectedHighlight,
  getShowHighlights
} from 'app/redux/slices/settings-action';
import { SERVICE_NAMES } from 'app/utils/consts';
import { ContractSummarizer } from 'app/components/Sections/ContractSummarizer';
import { CoverageCheckView } from 'pages/CoverageCheck/v1/CoverageCheckView';
import { RetentionCall } from 'app/components/Sections/RetentionCall';
import { CoverageCheckV2SummaryView } from 'pages/CoverageCheck';
import { IGPRView } from 'pages/IGPR';
import { AssuranceReportDraftingView } from 'pages/AssuranceReportDrafting';
import { AskYourDocsView } from 'pages/AskYourDocs';
import { TypeOfLossView } from 'pages';

export const SummaryView: React.FC = () => {
  const isLoading = useAppSelector(getIsLoading);
  const currentService = useAppSelector(getService);
  const selectedHighlight = useAppSelector(getSelectedHighlight);
  const highlightCoords = useAppSelector(getHighlightCoords);
  const showHighlights = useAppSelector(getShowHighlights);
  const currentFile = useAppSelector(getCurrentFile);

  useEffect(() => scrollToTop(), []);

  const usesPDFPreviewServices = new Set([
    SERVICE_NAMES.CONTRACT_INSIGHTS,
    SERVICE_NAMES.CLAIMS_INSIGHTS,
    SERVICE_NAMES.MEDICAL_CLAIMS_SUMMARY,
    SERVICE_NAMES.CONTRACT_SUMMARIZER,
    SERVICE_NAMES.INVOICE_EXTRACTOR
  ]);

  const haveHighlightsFeatureServices = new Set([
    SERVICE_NAMES.CONTRACT_INSIGHTS,
    SERVICE_NAMES.CLAIMS_INSIGHTS
  ]);

  const renderFilePreviewSection = (): React.JSX.Element => {
    const isPdfPreviewService =
      currentService && usesPDFPreviewServices.has(currentService);
    const isPdf = currentFile?.type === 'application/pdf';
    if (isPdfPreviewService && isPdf && currentFile) {
      return (
        <>
          <UploadedDocument />
          <PDFHighlightPreview
            selectedHighlight={selectedHighlight}
            highlightCoords={highlightCoords}
            showHighlights={
              currentService &&
              haveHighlightsFeatureServices.has(currentService)
                ? showHighlights
                : false
            }
          />
        </>
      );
    } else if (
      currentService &&
      currentService !== SERVICE_NAMES.CLAIMS_EMAIL_SUMMARY
    ) {
      return (
        <>
          <UploadedDocument />
          <PDFPreview />
        </>
      );
    } else {
      return (
        <>
          <UploadedText />
          <TextInput />
        </>
      );
    }
  };

  const renderSummarySection = (): React.JSX.Element => {
    switch (currentService) {
      case SERVICE_NAMES.CONTRACT_SUMMARIZER:
        return <ContractSummarizer />;
      case SERVICE_NAMES.INVOICE_EXTRACTOR:
        return <InvoiceExtractorSummary />;

      default:
        return <SummarySection />;
    }
  };

  const renderSummaryView = (): React.JSX.Element => {
    switch (currentService) {
      case SERVICE_NAMES.TRANSLATOR:
        return <TranslatorPage />;
      case SERVICE_NAMES.RETENTION_CALL:
        return <RetentionCall />;
      case SERVICE_NAMES.CATASTROPHE_DETECTION:
        return <CatastropheDetection />;
      case SERVICE_NAMES.DOCUMENT_COMPARE_V2:
        return <DocumentCompareV2 />;
      case SERVICE_NAMES.COVERAGE_CHECK:
        return <CoverageCheckView />;
      case SERVICE_NAMES.IGPR_SCORE:
        return <IGPRView />;
      case SERVICE_NAMES.COVERAGE_CHECK_V2:
        return <CoverageCheckV2SummaryView />;
      case SERVICE_NAMES.ASSURANCE_REPORT_DRAFTING:
        return <AssuranceReportDraftingView />;
      case SERVICE_NAMES.ASK_YOUR_DOCS:
        return <AskYourDocsView />;
      case SERVICE_NAMES.NDA_GENERATOR:
        return <NDAGenerator />;
      case SERVICE_NAMES.TYPE_OF_LOSS:
        return <TypeOfLossView />;
      default:
        return (
          <div className={styles.wrapper}>
            <div>
              <div className={styles.display}>{renderFilePreviewSection()}</div>
            </div>
            <div>{renderSummarySection()}</div>
          </div>
        );
    }
  };

  return (
    <div className={styles.summary}>
      {renderSummaryView()}
      <Footer />
      {isLoading && <LoadingSpinner />}
    </div>
  );
};

export default SummaryView;
